import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { Chart, registerables } from 'chart.js';

import { TimeFrameMode } from 'src/app/shared/interfaces/global-filter-model';
import { MondelezDataService } from 'src/app/shared/services/mondelez-data.service';
import { PrecisionType } from 'src/app/shared/interfaces/chart.model';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';

Chart.register(...registerables);

@Component({
  selector: 'app-revenue-chart',
  templateUrl: './revenue-chart.component.html',
  styleUrls: ['./revenue-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevenueChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() revenueData: any;
  @Input() capexData: any;
  @Input() showTimeFrameModeToggle = true;

  public aggregatedData = [];
  public regionKeys: any = [];
  private yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  public timeFrameMode: TimeFrameMode;
  public chartParams = [];
  public chartData = {
    year: {},
    quarter: {},
  };

  private subs = new Subscription();

  constructor(
    private mondelezDataService: MondelezDataService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.timeFrameMode) {
      this.prepareChart();
      this.aggregatedData = this.aggregator();
    }
  }

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      }),
    );
    this.subs.add(
      this.mondelezDataService.timeFrameMode.subscribe((value) => {
        this.timeFrameMode = value;
        this.prepareChart();
        this.aggregatedData = this.aggregator();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private prepareChart() {
    this.chartParams = [
      {
        name: 'capex',
        type: 'line',
        label: 'CAPEX as a % Revenue',
        color: '#6C4688',
        border: '#6C4688',
        bg: '#EAEAF2',
        stacked: true,
        precision: PrecisionType.NONE,
        legend: { isTop: true, isRight: false },
      },
      // {
      //   name: 'revenue',
      //   type: 'bar',
      //   label: 'Revenue',
      //   color: '#C4C4CD',
      //   stacked: true,
      //   titleY: 'Revenue ($ MM)',
      // },
    ];
    this.regionKeys = Object.keys(this.revenueData.year).filter((ele) => ele != 'total');
    const backgroundColors = ['#2E2E38 ', '#747480', '#C4C4CD', '#E1E1E6'];

    let dataSliceIndex = 0;
    if (this.revenueData[this.timeFrameMode]['total'].length > this.getTimeRangeLable().length) {
      dataSliceIndex = 1;
    }
    this.regionKeys.forEach((key, index) => {
      this.chartParams.push({
        name: key,
        type: 'bar',
        label: key + ' Revenue',
        color: backgroundColors[index],
        stacked: true,
        titleY: 'Revenue ($ MM)',
      });
      this.chartData[this.timeFrameMode][key] = this.revenueData[this.timeFrameMode][key].slice(dataSliceIndex);
    });

    this.chartData[this.timeFrameMode]['capex'] = this.capexVsRevenue();
    // this.chartData[this.timeFrameMode]['revenue'] = this.revenueData[this.timeFrameMode]['total'];
  }

  private aggregator() {
    const capexVsRevenue = this.capexVsRevenue();

    if (this.revenueData != undefined) {
      const countryList = Object.keys(this.revenueData[this.timeFrameMode]).filter((el) => el != 'total');
      const aggData = countryList.map((el) => {
        let dataSliceIndex = 0;
        if (this.revenueData[this.timeFrameMode]['total'].length > this.getTimeRangeLable().length) {
          dataSliceIndex = 1;
        }
        const sum = this.revenueData[this.timeFrameMode][el][this.revenueData[this.timeFrameMode][el].length - 1] - this.revenueData[this.timeFrameMode][el][dataSliceIndex];
        return { label: el, value: this.formatNumbers(sum), prefix: '', suffix: 'MM lbs' };
      });

      aggData.push(
        {
          label: 'Revenue CAGR',
          value: this.revenueData['cagr'] * 100,
          prefix: '',
          suffix: '%',
        },
        {
          label: 'CAPEX as a % of Revenue',
          value: capexVsRevenue.reduce((sum, item) => sum + item, 0) / capexVsRevenue.length,
          prefix: '',
          suffix: '%',
        },
      );
      return aggData;
    } else {
      return [];
    }
  }

  private capexVsRevenue() {
    let i = 0;
    return this.revenueData[this.timeFrameMode]['total'].map((el) => {
      let per = (this.capexData[this.timeFrameMode]['total'][i] * 100) / el;
      i++;
      // return Math.round(per);
      return parseFloat(per.toFixed(2));
    });
  }

  onTimeFrameChange(value: TimeFrameMode) {
    this.mondelezDataService.updateTimeFrameMode(value);
  }

  private formatNumbers(amt: number) {
    if (!amt) return 0;
    let millionth = amt / 1000000;
    return parseFloat(millionth.toFixed(1));
  }

  getTimeRangeLable() {
    const labels = [];
    for (let year = this.yearRange.startYear; year < this.yearRange.endYear + 1; year++) {
      if (this.timeFrameMode == TimeFrameMode.QUARTERLY) {
        for (let q = 1; q <= 4; q++) {
          labels.push(year + '-Q' + q);
        }
      } else {
        labels.push(year);
      }
    }
    return labels;
  }
}
